/* GF V2.5.3. Adapter contract inspected in Printify personalize-button.js (printify-29).
 * No private API requests, storage flags, or image/button-text heuristics.
 * A new editor save, matching native state AND submitted property are all required.
 * This is storefront validation, not a server-side checkout guarantee.
 */
if (!customElements.get('gf-v25-personalization')) {
  customElements.define('gf-v25-personalization', class extends HTMLElement {
    connectedCallback() {
      if (this.abort && !this.abort.signal.aborted) return;
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.consumedIds ||= new Set();
      this.explicit = this.dataset.preselected === 'true';
      this.saved = null;
      this.saveCandidate = null;
      this.attempt = null;
      this.pending = false;
      this.status = this.querySelector('[role="status"]');
      this.root.addEventListener('change', (event) => {
        if (!event.target.closest('variant-selects')) return;
        this.explicit = true;
        this.pending = true;
        this.changed = !!this.saved || this.changed;
        this.saved = null;
        this.saveCandidate = null;
        this.attempt = null;
        // Clear native state as well as our readiness; never reuse native variant history.
        this.instance?.clearPersonalization?.();
        this.sync();
      }, { ...options, capture: true });
      this.root.addEventListener('gf-v2:variant-settled', (event) => {
        this.pending = false;
        if (event.detail?.failed) this.explicit = false;
        this.sync();
      }, options);
      // Native radio pickers may already show the first size selected. An explicit
      // click on that same radio is still a valid customer choice.
      this.root.addEventListener('click', (event) => {
        if (!this.root.querySelector('gf-v2-purchase') && event.target.matches('variant-selects input[type=radio]')) {
          this.explicit = true;
          queueMicrotask(() => this.sync());
        }
      }, options);
      // Capture prevents inline Printify onclick and all theme submit handlers when gated.
      document.addEventListener('click', (event) => {
        const app = event.target.closest('[data-gf-personalize-app]');
        if (!app || !this.root.contains(app) || !event.target.closest('button')) return;
        if (!this.sizeReady()) {
          event.preventDefault(); event.stopImmediatePropagation(); this.sync(); return;
        }
        this.saved = null;
        this.saveCandidate = null;
        this.cartAdded = false;
        this.attempt = this.nativeState?.personalizationData ? null : { variant: this.variantId };
        this.sync();
      }, { ...options, capture: true });
      this.root.addEventListener('submit', (event) => {
        if (event.target !== this.form || this.canSubmit()) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.sync();
        this.status?.scrollIntoView({ block: 'nearest' });
      }, { ...options, capture: true });
      window.addEventListener('message', (event) => this.onSave(event), { ...options, capture: true });
      // Native method completions and DOM mutations drive reconciliation; no save delay.
      this.observer = new MutationObserver(() => { this.bindNative(); this.sync(); });
      this.observer.observe(this.root, { subtree: true, childList: true, attributes: true,
        attributeFilter: ['value', 'src', 'srcset', 'data-initialized'] });
      this.bindNative();
      this.sync();
    }
    bindNative() {
      const instance = this.instance;
      if (!instance?.handleSharedStateChange || this.boundInstance === instance) return;
      this.restoreNative?.();
      this.boundInstance = instance;
      const originals = new Map();
      const wrap = (name, fn) => {
        const original = instance[name];
        if (typeof original !== 'function') return;
        const replacement = (...args) => fn(original, ...args);
        originals.set(name, { original, replacement }); instance[name] = replacement;
      };
      // Printify's document-wide option lookup otherwise reads cart recommendation
      // colors/sizes before the PDP. Scope the existing resolver to its owning product.
      wrap('getCurrentSelectedOptions', original => {
        const groups = [...this.root.querySelectorAll('variant-selects [data-gf-option]')];
        if (!groups.length) return original();
        const options = {};
        groups.forEach(group => {
          const name = this.nativeState?.productData?.options[Number(group.dataset.gfPosition) - 1];
          const value = group.querySelector('input:checked, select')?.value;
          if (name && value) options[name] = value;
        });
        return options;
      });
      wrap('addPersonalizationToForm', (original, data) => {
        // Native Printify-29 clears lowercase names but inserts uppercase names.
        // Replace the old ID in owned forms, and undo only new writes to unrelated forms.
        const selector = 'input[name="properties[Personalization ID]"]';
        const outsiders = [...document.querySelectorAll('form[action*="/cart/add"]')].filter(f => !this.root.contains(f));
        const existing = new Set(outsiders.flatMap(f => [...f.querySelectorAll(selector)]));
        this.root.querySelectorAll('form[action*="/cart/add"]').forEach(f => f.querySelectorAll(selector).forEach(i => i.remove()));
        original(data);
        outsiders.forEach(f => f.querySelectorAll(selector).forEach(i => { if (!existing.has(i)) i.remove(); }));
      });
      wrap('handleSharedStateChange', (original, type, data) => {
        original(type, data);
        if (type === 'personalizationCleared' || (type === 'variantChanged' && !data?.hasPersonalization)) {
          this.saved = null; this.saveCandidate = null;
        }
        this.sync();
      });
      wrap('closePersonalizeModal', original => { original(); this.attempt = null; this.sync(); });
      wrap('replaceProductImage', (original, url) => {
        // Resolve from immutable Shopify IDs and side metadata, never the active
        // slide, first DOM image, or thumbnail position (all can change on navigation).
        const mediaId = this.frontMediaId();
        if (!mediaId) return;
        this.restorePreview();
        this.preview = { url, mediaId };
        this.syncPreview();
      });
      wrap('restoreProductImage', () => this.restorePreview());
      wrap('clearPersonalization', original => {
        this.saved = null; this.saveCandidate = null; this.attempt = null;
        this.restorePreview(); original(); this.sync();
      });
      instance.buttonTextSettings.withPersonalization = 'CHANGE YOUR NAME';
      if (this.nativeState?.personalizationData) instance.showPersonalizationStatus(this.nativeState.personalizationData.personalizationId);
      this.restoreNative = () => {
        originals.forEach(({ original, replacement }, name) => { if (instance[name] === replacement) instance[name] = original; });
        this.boundInstance = null;
      };
    }
    frontMediaId() {
      let mapping;
      try { mapping = JSON.parse(this.querySelector('[data-gf-personalization-media]')?.textContent || 'null'); }
      catch { return null; }
      if (!mapping?.media) return null;
      const colorValue = this.root.querySelector('[data-gf-color] input:checked, [data-gf-color] select')?.value || '';
      const color = colorValue.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const images = mapping.media.filter(media => media.type === 'image');
      const fronts = images.filter(media => media.side === 'front' &&
        (!color || [color, 'all', '-', 'linked-shared'].includes(media.color)));
      if (fronts.length === 1) return fronts[0].id;
      if (fronts.length > 1) return fronts.find(media => media.id === mapping.featuredId)?.id || null;
      // Missing/ambiguous side metadata must never cause another artwork to be
      // overwritten. Shopify's featured image can itself be the backprint.
      return null;
    }
    previewImages() {
      if (!this.preview) return [];
      const id = this.preview.mediaId;
      return [...this.root.querySelectorAll('img')].filter(image => {
        // Use the closest owner, not every ancestor's descendants. An outer gallery
        // or modal wrapper must never claim images owned by a different media ID.
        const owner = image.closest('[data-media-id], [data-target], [data-gf-media-id], .gf-v2-gallery__slide[data-id]');
        if (!owner) return false;
        const key = owner.dataset.mediaId || owner.dataset.target || owner.dataset.gfMediaId || owner.dataset.id;
        return key?.split('-').pop() === id;
      });
    }

    syncPreview() {
      if (!this.preview || !this.nativeState?.personalizationData) return;
      this.previewOriginals ||= new Map();
      this.previewImages().forEach(image => {
        if (!this.previewOriginals.has(image)) this.previewOriginals.set(image, {
          src: image.dataset.originalSrc || image.getAttribute('src'), srcset: image.getAttribute('srcset')
        });
        if (image.getAttribute('src') !== this.preview.url) image.setAttribute('src', this.preview.url);
        if (image.getAttribute('srcset')) image.setAttribute('srcset', '');
      });
    }
    restorePreview() {
      this.previewOriginals?.forEach((attrs, image) => {
        if (attrs.src !== null) image.setAttribute('src', attrs.src);
        if (attrs.srcset === null) image.removeAttribute('srcset'); else image.setAttribute('srcset', attrs.srcset);
      });
      this.previewOriginals?.clear(); this.preview = null;
    }
    onCartAdded(formData) {
      // Called only after Shopify accepts the captured FormData. Never reset size.
      const id = formData.get('properties[Personalization ID]');
      if (id) this.consumedIds.add(id);
      if (id !== this.saved?.id) return;
      this.instance?.clearPersonalization();
      this.cartAdded = true;
      this.sync();
    }
    get form() { return document.getElementById(this.dataset.formId); }
    get variantId() { return this.form?.querySelector('[name="id"]')?.value || ''; }
    get container() { return this.root?.querySelector('[data-gf-personalize-app] .personalize-container[data-block-id]'); }
    get instance() { return this.container ? window[`personalize_${this.container.dataset.blockId}`] : null; }
    get nativeState() { return window.personalizationSharedState; }
    sizeReady() {
      const purchase = this.root.querySelector('gf-v2-purchase');
      const explicit = purchase ? purchase.explicitSize : this.explicit;
      const variant = this.instance?.getCurrentSelectedVariant?.();
      return !!(explicit && !this.pending && !purchase?.pending && !purchase?.sizeUnavailable &&
        this.variantId && variant?.available && String(variant.id) === this.variantId &&
        String(this.nativeState?.productData?.id) === this.dataset.productId);
    }
    canSubmit() {
      if (!this.sizeReady() || !this.saved || this.saved.variant !== this.variantId) return false;
      const state = this.nativeState;
      if (String(state?.currentVariant?.id) !== this.variantId || state?.personalizationData?.personalizationId !== this.saved.id) return false;
      const values = new FormData(this.form).getAll('properties[Personalization ID]');
      return values.length > 0 && values.every(value => value === this.saved.id);
    }
    onSave(event) {
      if (event.data?.type !== 'PERSONALIZATION_APPLIED' || !this.attempt) return;
      const instance = this.instance;
      const frame = instance && document.getElementById(instance.elementIds.iframe);
      // Both the exact editor window and verified editor origin must match.
      if (!frame || event.source !== frame.contentWindow || event.origin !== 'https://personalize.at') return;
      const id = event.data.data?.personalizationId;
      const attempt = this.attempt;
      this.attempt = null;
      if (typeof id !== 'string' || !id.trim() || this.consumedIds.has(id) || !this.sizeReady() || attempt.variant !== this.variantId) return;
      // Browsers may run microtasks between this capture listener and Printify's
      // listener. Reconcile the authenticated save after native state/form writes.
      // Button labels and preview images are never evidence of a valid save.
      this.saveCandidate = { id, variant: attempt.variant };
      queueMicrotask(() => { if (this.isConnected) this.sync(); });
    }
    reconcileSave() {
      const candidate = this.saveCandidate;
      if (!candidate) return;
      if (this.pending || candidate.variant !== this.variantId) {
        this.saveCandidate = null;
        return;
      }
      this.saved = candidate;
      if (this.canSubmit()) {
        this.saveCandidate = null;
        this.changed = false;
      } else {
        this.saved = null;
      }
    }

    sync() {
      if (!this.root || !this.form) return;
      this.reconcileSave();
      this.syncPreview();
      const sizeReady = this.sizeReady();
      const ready = this.canSubmit();
      this.dataset.state = !sizeReady ? 'size-required' : ready ? 'saved' : this.attempt ? 'editor-open' : this.changed ? 'variant-changed' : this.cartAdded ? 'cart-added' : 'personalization-required';
      this.root.querySelectorAll('[data-gf-personalize-app]').forEach(app => { app.disabled = !sizeReady; });
      let message = !sizeReady ? 'SELECT YOUR SIZE FIRST' : this.changed ? 'SIZE OR COLOR CHANGED — PERSONALIZE AGAIN' : 'PERSONALIZE YOUR TEE AND SAVE YOUR DESIGN';
      if (!this.instance) message = 'PERSONALIZATION IS UNAVAILABLE — PLEASE TRY AGAIN';
      if (ready) message = 'PERSONALIZATION SAVED — READY TO ADD TO CART';
      if (this.status && this.status.textContent !== message) this.status.textContent = message;
      const main = this.form.querySelector('[name="add"]');
      const submitting = main?.classList.contains('loading');
      if (main && !submitting) {
        const disabled = String(!ready);
        if (main.getAttribute('aria-disabled') !== disabled) main.setAttribute('aria-disabled', disabled);
        const label = main.querySelector('span');
        const text = ready ? (window.variantStrings?.addToCart || 'ADD TO CART') : !sizeReady ? 'SELECT YOUR SIZE FIRST' : 'PERSONALIZE FIRST';
        if (!main.disabled && label && label.textContent.trim() !== text) label.textContent = text;
      }
      const sticky = this.root.querySelector('.gf-v2-sticky button');
      if (sticky && !ready) {
        sticky.disabled = true;
        if (sticky.textContent !== message) sticky.textContent = message;
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.abort?.abort(); this.restoreNative?.();
      this.saved = null; this.saveCandidate = null; this.attempt = null;
    }
  });
}
